import React from 'react';
import { ExternalLink, Github, Star, Users } from 'lucide-react';

const Projects = () => {
  const projects = [
    {
      title: "AI Campus Assistant",
      description: "A comprehensive chatbot that helps students navigate campus resources, find study groups, and get academic support using natural language processing.",
      image: "/api/placeholder/400/250",
      tech: ["Python", "NLP", "Flask", "OpenAI API"],
      features: ["Voice Recognition", "Multi-language Support", "Smart Scheduling"],
      students: 4,
      rating: 4.8
    },
    {
      title: "Smart Study Planner",
      description: "AI-powered study schedule optimizer that analyzes learning patterns and creates personalized study plans for maximum retention.",
      image: "/api/placeholder/400/250", 
      tech: ["Machine Learning", "React", "TensorFlow", "MongoDB"],
      features: ["Adaptive Learning", "Progress Tracking", "Collaboration Tools"],
      students: 3,
      rating: 4.9
    },
    {
      title: "AI Tutor for STEM",
      description: "Personalized AI tutor that provides step-by-step explanations for math, physics, and computer science problems with visual learning aids.",
      image: "/api/placeholder/400/250",
      tech: ["Deep Learning", "Computer Vision", "PyTorch", "React"],
      features: ["Visual Problem Solving", "Adaptive Difficulty", "Performance Analytics"],
      students: 5,
      rating: 4.7
    },
    {
      title: "Course Recommendation Engine",
      description: "Machine learning system that recommends courses and career paths based on student interests, performance, and industry trends.",
      image: "/api/placeholder/400/250",
      tech: ["Recommendation Systems", "Data Science", "Pandas", "Scikit-learn"],
      features: ["Predictive Analytics", "Industry Insights", "Skill Gap Analysis"],
      students: 3,
      rating: 4.6
    },
    {
      title: "AI-Powered Research Assistant",
      description: "Research tool that helps students find relevant papers, summarize content, and generate citations using advanced NLP techniques.",
      image: "/api/placeholder/400/250",
      tech: ["NLP", "Web Scraping", "Transformers", "FastAPI"],
      features: ["Auto-Summarization", "Citation Generation", "Plagiarism Detection"],
      students: 4,
      rating: 4.8
    },
    {
      title: "Mental Health Support Bot",
      description: "Empathetic AI companion that provides mental health resources, mood tracking, and connects students with professional help when needed.",
      image: "/api/placeholder/400/250",
      tech: ["Sentiment Analysis", "Psychology AI", "React Native", "Cloud ML"],
      features: ["Mood Analysis", "Crisis Detection", "Resource Matching"],
      students: 6,
      rating: 4.9
    }
  ];

  return (
    <section id="projects" className="py-20 bg-gray-900 relative">
      {/* Background elements */}
      <div className="absolute inset-0 bg-circuit-pattern opacity-5"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white futuristic-text mb-6">
            Student Projects
          </h2>
          <div className="h-1 w-24 bg-gradient-to-r from-transparent via-orange-500 to-transparent mx-auto mb-8 glow-line"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Real projects built by our students during the bootcamp. These showcase the practical skills 
            and innovative thinking developed in just 14 days.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <div 
              key={index}
              className="bg-black/50 backdrop-blur-sm rounded-2xl border border-orange-500/20 overflow-hidden hover:border-orange-500/40 transition-all duration-300 group glow-card-hover"
            >
              {/* Project Image Placeholder */}
              <div className="h-48 bg-gradient-to-br from-gray-800 to-gray-900 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mb-3 mx-auto">
                      <Star className="w-8 h-8 text-orange-500" />
                    </div>
                    <span className="text-gray-400 text-sm">Project Demo</span>
                  </div>
                </div>
                
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="flex space-x-4">
                    <button className="p-2 bg-orange-500/20 rounded-full hover:bg-orange-500/30 transition-colors">
                      <ExternalLink className="w-5 h-5 text-orange-500" />
                    </button>
                    <button className="p-2 bg-orange-500/20 rounded-full hover:bg-orange-500/30 transition-colors">
                      <Github className="w-5 h-5 text-orange-500" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-6">
                {/* Title and Rating */}
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-xl font-bold text-white futuristic-text group-hover:text-orange-500 transition-colors">
                    {project.title}
                  </h3>
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    <span className="text-yellow-500 text-sm font-medium">{project.rating}</span>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  {project.description}
                </p>

                {/* Features */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-white mb-2">Key Features:</h4>
                  <div className="flex flex-wrap gap-2">
                    {project.features.map((feature, idx) => (
                      <span 
                        key={idx}
                        className="px-2 py-1 bg-orange-500/10 text-orange-400 text-xs rounded-full border border-orange-500/20"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Tech Stack */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-white mb-2">Tech Stack:</h4>
                  <div className="flex flex-wrap gap-2">
                    {project.tech.map((tech, idx) => (
                      <span 
                        key={idx}
                        className="px-2 py-1 bg-gray-700/50 text-gray-300 text-xs rounded border border-gray-600"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Team Info */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-400 text-sm">{project.students} Students</span>
                  </div>
                  <div className="flex space-x-2">
                    <button className="text-orange-500 hover:text-orange-400 text-sm font-medium transition-colors">
                      View Details
                    </button>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-4 h-1 bg-gray-800 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-orange-500 to-orange-400 w-0 group-hover:w-full transition-all duration-1000 ease-out"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Success Stories Section */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white futuristic-text mb-4">Success Stories</h3>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Our students don't just learn AI - they create solutions that make a real impact.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                stat: "85%",
                label: "Projects Deployed",
                desc: "Successfully launched in production"
              },
              {
                stat: "92%",
                label: "Student Satisfaction",
                desc: "Would recommend to others"
              },
              {
                stat: "67%",
                label: "Career Impact",
                desc: "Landed AI-related internships"
              }
            ].map((item, index) => (
              <div key={index} className="text-center bg-black/30 backdrop-blur-sm p-6 rounded-xl border border-orange-500/20">
                <div className="text-4xl font-bold text-orange-500 futuristic-text mb-2">{item.stat}</div>
                <div className="text-lg font-semibold text-white mb-1">{item.label}</div>
                <div className="text-gray-400 text-sm">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;